import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface CampaignCallStats {
  [campaignId: string]: {
    totalCalls: number;
    conversions: number;
    transfers: number;
    messages: number;
    handRaised: number;
  };
}

export function useCampaignCallStats(
  allCampaignIds?: string[],
  timeRange?: { days?: number },
  enabled: boolean = true
) {
  const [campaignStats, setCampaignStats] = useState<CampaignCallStats>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      if (!user || !enabled) {
        setCampaignStats({});
        setLoading(false);
        return;
      }

      if (allCampaignIds !== undefined && allCampaignIds.length === 0) {
        setCampaignStats({});
        setLoading(false);
        return;
      }

      try {
        setError(null);

        const campaignIds = allCampaignIds ?? [];

        let since: string | null = null;
        if (timeRange?.days) {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - timeRange.days);
          since = cutoff.toISOString();
        }

        const { data, error: rpcError } = await supabase.rpc(
          'get_campaign_call_stats',
          {
            p_campaign_ids: campaignIds,
            p_user_id: user.id,
            p_since: since,
          }
        );

        if (rpcError) throw rpcError;

        // Seed every requested campaign with zeros so callers never get undefined
        const stats: CampaignCallStats = {};
        campaignIds.forEach((id) => {
          stats[id] = { totalCalls: 0, conversions: 0, transfers: 0, messages: 0, handRaised: 0 };
        });

        // Overwrite zeros with real aggregated values from the server
        (data ?? []).forEach((row: {
          campaign_id: string;
          total_calls: number;
          conversions: number;
          transfers: number;
          messages: number;
          hand_raised: number;
        }) => {
          stats[row.campaign_id] = {
            totalCalls: Number(row.total_calls),
            conversions: Number(row.conversions),
            transfers: Number(row.transfers),
            messages: Number(row.messages),
            handRaised: Number(row.hand_raised),
          };
        });

        setCampaignStats(stats);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load call stats';
        setError(message);
        setCampaignStats({});
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, allCampaignIds, timeRange, enabled]);

  return { campaignStats, loading, error };
}
